/**
 * TD Subscription Box 2026
 * Unified script for the 2026 subscription box buy experience
 * Contains both the purchase type selector and quantity selector logic
 * Scoped to only affect td-subscription-box-2026.liquid
 */

/* ==========================================================================
   TD Sub Box 2026 - Scope resolution

   The quantity selector, subscription selector and add-to-cart button can be
   rendered two ways:

   - Legacy (`sub_box_2026`): all three parts share one <product-form> ancestor.
   - Split blocks: each block root carries data-td-sub-box="<section id>" and
     the parts are siblings, so the section id is the only shared handle.

   Every cross-part lookup goes through this helper so both layouts resolve the
   same way and cross-section events can be filtered.

   Assigned through `window` and guarded, because each block that renders a sub
   box part also includes this asset, so the script can execute more than once.
   ========================================================================== */

window.TdSubBox = window.TdSubBox || {
  /**
   * Stable key shared by every part of one sub box, used to scope events.
   * @param {HTMLElement} el
   * @returns {string|null}
   */
  key(el) {
    const sectionId = el.closest('[data-td-sub-box]')?.dataset.tdSubBox;
    if (sectionId) return sectionId;
    return el.closest('product-form')?.id || null;
  },

  /**
   * Every root belonging to the same sub box as `el`.
   * @param {HTMLElement} el
   * @returns {HTMLElement[]}
   */
  roots(el) {
    const sectionId = el.closest('[data-td-sub-box]')?.dataset.tdSubBox;
    if (sectionId) {
      return Array.from(document.querySelectorAll(`[data-td-sub-box="${sectionId}"]`));
    }
    const host = el.closest('product-form');
    return host ? [host] : [];
  },

  /**
   * First match for `selector` anywhere in the sub box.
   * @param {HTMLElement} el
   * @param {string} selector
   * @returns {HTMLElement|null}
   */
  find(el, selector) {
    const roots = TdSubBox.roots(el);
    for (const root of roots) {
      if (root.matches(selector)) return root;
      const match = root.querySelector(selector);
      if (match) return match;
    }
    return null;
  },

  /**
   * The <product-form> that owns the add-to-cart form for this sub box.
   * Resolved by id rather than by tag, because bundle modals render their own
   * <product-form> and must never be mistaken for the add-to-cart block.
   * @param {HTMLElement} el
   * @returns {HTMLElement|null}
   */
  host(el) {
    const sectionId = el.closest('[data-td-sub-box]')?.dataset.tdSubBox;
    if (sectionId) return document.getElementById(`add-to-cart-${sectionId}`);
    return el.closest('product-form');
  },

  /**
   * True when `event` came from the same sub box as `el`.
   * @param {HTMLElement} el
   * @param {CustomEvent} event
   * @returns {boolean}
   */
  ownsEvent(el, event) {
    const eventKey = event?.detail?.scopeKey;
    if (!eventKey) return true;
    return eventKey === TdSubBox.key(el);
  }
};

/* ==========================================================================
   TD Subscription Box - Purchase Type Selector
   ========================================================================== */

if (!customElements.get('td-subscription-box')) {
  class TdSubscriptionBox extends HTMLElement {
    constructor() {
      super();
      
      /* State */
      this.purchaseType = 'onetime'; // 'onetime' or 'subscription'
      this.selectedSellingPlanId = null;
      
      /* DOM references */
      this.radioOnetime = null;
      this.radioSubscription = null;
      this.frequencySelect = null;
      
      /* Price elements */
      this.onetimePrice = null;
      this.subscriptionPrice = null;

      /* Breakpoint used to split mobile vs desktop behavior */
      this.desktopMediaQuery = window.matchMedia('(min-width: 835px)');
      this.frequencyActivationPending = false;
    }

    connectedCallback() {
      this.initElements();
      this.setupEventListeners();
      this.setInitialState();
      this.dispatchShippingCalloutUpdate();
    }

    /**
     * Initialize DOM element references
     */
    initElements() {
      /* Radio inputs */
      this.radioOnetime = this.querySelector('[data-purchase-type="onetime"]');
      this.radioSubscription = this.querySelector('[data-purchase-type="subscription"]');
      
      /* Frequency dropdown */
      this.frequencySelect = this.querySelector('[data-frequency-select]');
      
      /* Price display elements */
      this.onetimePrice = this.querySelector('[data-onetime-price]');
      this.subscriptionPrice = this.querySelector('[data-subscription-price]');
      
      /* Option containers for styling */
      this.onetimeOption = this.querySelector('.td-purchase-option--onetime');
      this.subscriptionOption = this.querySelector('.td-purchase-option--subscription');
    }

    /* Nodes below live in the add-to-cart block once the parts are split apart,
       so they are resolved on every access rather than cached. */

    /** @returns {HTMLElement|null} */
    get productFormHost() {
      return TdSubBox.host(this);
    }

    /** @returns {HTMLInputElement|null} */
    get sellingPlanInput() {
      return TdSubBox.find(this, 'input[name="selling_plan"]');
    }

    /** @returns {HTMLInputElement|null} */
    get purchaseTypeTrackingInput() {
      return TdSubBox.find(this, '[data-td-purchase-type-input]');
    }

    /**
     * Set up event listeners for radio buttons, option containers, and frequency dropdown
     */
    setupEventListeners() {
      /* Keep component state in sync with radio changes (including programmatic changes) */
      if (this.radioOnetime) {
        this.radioOnetime.addEventListener('change', () => {
          if (this.radioOnetime.checked) {
            this.handlePurchaseTypeChange('onetime');
          }
        });
      }

      if (this.radioSubscription) {
        this.radioSubscription.addEventListener('change', () => {
          if (this.radioSubscription.checked) {
            this.handlePurchaseTypeChange('subscription');
          }
        });
      }

      /* Make entire option containers clickable */
      if (this.onetimeOption) {
        this.onetimeOption.addEventListener('click', (e) => {
          /* Don't trigger if clicking on frequency dropdown */
          if (e.target.closest('[data-frequency-select]')) return;
          this.handlePurchaseTypeChange('onetime');
          if (this.radioOnetime) {
            this.radioOnetime.checked = true;
          }
        });
      }
      
      if (this.subscriptionOption) {
        this.subscriptionOption.addEventListener('click', (e) => {
          /* Don't trigger if clicking on frequency dropdown or if disabled */
          if (e.target.closest('[data-frequency-select]')) return;
          /* The rewards disclosure only reveals information - opening it must
             not change what the shopper is buying. Keyboard activation of a
             button dispatches a bubbling click, so this covers Enter/Space. */
          if (e.target.closest('[data-td-rewards-toggle], [data-td-rewards-panel]')) return;
          if (this.subscriptionOption.classList.contains('td-purchase-option--disabled')) return;
          this.handlePurchaseTypeChange('subscription');
          if (this.radioSubscription) {
            this.radioSubscription.checked = true;
          }
        });
      }
      
      /* Frequency dropdown change */
      if (this.frequencySelect) {
        const activateSubscriptionFromSelect = () => {
          if (this.frequencyActivationPending) return;
          this.frequencyActivationPending = true;
          requestAnimationFrame(() => {
            this.frequencyActivationPending = false;
          });

          const subscriptionDisabled = this.subscriptionOption?.classList.contains('td-purchase-option--disabled');
          if (subscriptionDisabled || this.purchaseType === 'subscription') return;

          this.handlePurchaseTypeChange('subscription');
          if (this.radioSubscription) {
            this.radioSubscription.checked = true;
          }
        };

        this.frequencySelect.addEventListener('mousedown', activateSubscriptionFromSelect);
        this.frequencySelect.addEventListener('touchstart', activateSubscriptionFromSelect, { passive: true });
        this.frequencySelect.addEventListener('focus', activateSubscriptionFromSelect);
        this.frequencySelect.addEventListener('change', () => this.handleFrequencyChange());
      }

      /* Quantity selector can toggle subscription availability; refresh UI lock state */
      this.addEventListener('td-subscription-option:state-change', () => {
        this.updateUI();
      });
    }

    /**
     * Set initial state based on URL params or defaults
     */
    setInitialState() {
      const url = new URL(window.location.href);
      const urlSellingPlan = url.searchParams.get('selling_plan');
      const hasSellingPlanOptions =
        this.frequencySelect && this.frequencySelect.options && this.frequencySelect.options.length > 0;

      if (hasSellingPlanOptions) {
        const defaultOptionIndex = Number(this.frequencySelect.dataset.defaultSellingPlanIndex);
        const preferredOption =
          Number.isInteger(defaultOptionIndex) && defaultOptionIndex >= 0
            ? this.frequencySelect.options[defaultOptionIndex]
            : null;
        this.selectedSellingPlanId =
          preferredOption?.value || this.frequencySelect.options[0].value;
      }
      
      /* Only honour a plan the picker actually offers. Assigning an unknown id
         leaves the select on selectedIndex -1 while the hidden input still
         carries it, so the shopper would see a blank cadence and check out on a
         plan the merchant excluded. */
      const offersUrlSellingPlan =
        hasSellingPlanOptions &&
        Array.from(this.frequencySelect.options).some((option) => option.value === urlSellingPlan);

      if (offersUrlSellingPlan) {
        /* URL has a selling plan - select subscription */
        this.purchaseType = 'subscription';
        this.selectedSellingPlanId = urlSellingPlan;
        
        if (this.radioSubscription) {
          this.radioSubscription.checked = true;
        }
        
        if (this.frequencySelect) {
          this.frequencySelect.value = urlSellingPlan;
        }
      } else {
        const preferredSelection = this.getPreferredDefaultSelection();

        /* Default selection by customer type */
        this.purchaseType = preferredSelection;
        if (this.purchaseType === 'subscription' && !hasSellingPlanOptions) {
          this.purchaseType = 'onetime';
        }
        
        if (this.purchaseType === 'subscription' && this.radioSubscription) {
          this.radioSubscription.checked = true;
        } else if (this.radioOnetime) {
          this.radioOnetime.checked = true;
        }
      }
      
      this.updateUI();
      this.syncSellingPlanInput();
      this.updateATCPrice();
    }

    getPreferredOpenMode() {
      const customerType = this.dataset.customerType === 'returning' ? 'returning' : 'new';
      if (customerType === 'returning') {
        return this.dataset.subscriptionOpenReturningCustomers || 'closed_both';
      }
      return this.dataset.subscriptionOpenNewCustomers || 'closed_both';
    }

    shouldOpenForCurrentViewport(mode) {
      const isDesktop = this.desktopMediaQuery.matches;

      if (mode === 'open_both') return true;
      if (mode === 'open_mobile_only') return !isDesktop;
      if (mode === 'open_desktop_only') return isDesktop;
      return false;
    }

    getPreferredDefaultSelection() {
      const customerType = this.dataset.customerType === 'returning' ? 'returning' : 'new';
      const preferred =
        customerType === 'returning'
          ? this.dataset.defaultSelectionReturning
          : this.dataset.defaultSelectionNew;

      return preferred === 'subscription' ? 'subscription' : 'onetime';
    }

    /**
     * Handle purchase type radio button change
     * @param {string} type - 'onetime' or 'subscription'
     */
    handlePurchaseTypeChange(type) {
      this.purchaseType = type;
      
      if (type === 'subscription' && this.frequencySelect) {
        /* Use currently selected frequency */
        this.selectedSellingPlanId = this.frequencySelect.value;
      }
      
      this.updateUI();
      this.syncSellingPlanInput();
      this.updateATCPrice();
      this.dispatchChangeEvent();
    }

    /**
     * Handle frequency dropdown change
     */
    handleFrequencyChange() {
      if (!this.frequencySelect) return;
      
      this.selectedSellingPlanId = this.frequencySelect.value;
      
      /* If changing frequency, ensure subscription is selected */
      if (this.purchaseType !== 'subscription') {
        this.purchaseType = 'subscription';
        if (this.radioSubscription) {
          this.radioSubscription.checked = true;
        }
      }
      
      this.updateUI();
      this.syncSellingPlanInput();
      this.updateATCPrice();
      this.dispatchChangeEvent();
    }

    /**
     * Update visual UI state
     */
    updateUI() {
      /* Update option container active states */
      if (this.onetimeOption) {
        this.onetimeOption.classList.toggle('td-purchase-option--active', this.purchaseType === 'onetime');
      }
      
      if (this.subscriptionOption) {
        this.subscriptionOption.classList.toggle('td-purchase-option--active', this.purchaseType === 'subscription');
      }
      
      /* Show/hide frequency dropdown based on selection */
      const subscriptionContent = this.querySelector('.td-subscription-content');
      if (subscriptionContent) {
        const hasSellingPlanOptions =
          this.frequencySelect && this.frequencySelect.options && this.frequencySelect.options.length > 0;
        const forceOpenSubscriptionContent =
          hasSellingPlanOptions && this.shouldOpenForCurrentViewport(this.getPreferredOpenMode());
        const isSubscriptionOpen = this.purchaseType === 'subscription' || forceOpenSubscriptionContent;

        subscriptionContent.classList.toggle(
          'td-subscription-content--visible',
          isSubscriptionOpen
        );

        if (this.subscriptionOption) {
          this.subscriptionOption.classList.toggle('td-purchase-option--show-closed-text', !isSubscriptionOpen);
        }
      }

      /* Frequency selector should only be interactive when subscription is active */
      if (this.frequencySelect) {
        const subscriptionDisabled = this.subscriptionOption?.classList.contains('td-purchase-option--disabled');
        this.frequencySelect.disabled = !!subscriptionDisabled;
      }
    }

    /**
     * Sync the hidden selling_plan input with our selection
     */
    syncSellingPlanInput() {
      const sellingPlanInput = this.sellingPlanInput;
      if (!sellingPlanInput) return;

      sellingPlanInput.value =
        this.purchaseType === 'subscription' && this.selectedSellingPlanId
          ? this.selectedSellingPlanId
          : '';

      const purchaseTypeTrackingInput = this.purchaseTypeTrackingInput;
      if (purchaseTypeTrackingInput) {
        purchaseTypeTrackingInput.value = this.purchaseType === 'subscription' ? 'subscription' : 'one_time';
      }
      
      /* Trigger change event so quantity selector can react */
      sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Keep the ATC button price in sync with the selected purchase type.
     */
    updateATCPrice() {
      const productFormHost = this.productFormHost;
      const activeQuantityButton = TdSubBox.find(this, '.td-quantity-button--active');
      const atcPriceElement = TdSubBox.find(this, '[data-quantity-price]');
      const atcComparePriceElement = TdSubBox.find(this, '[data-quantity-compare-price]');

      if (!atcPriceElement) return;

      let price = '';
      let comparePrice = '';

      if (this.purchaseType === 'subscription') {
        price =
          activeQuantityButton?.getAttribute('data-subscription-price') ||
          productFormHost?.getAttribute('data-subscription-price') ||
          '';
        comparePrice =
          activeQuantityButton?.getAttribute('data-subscription-compare-price') ||
          productFormHost?.getAttribute('data-subscription-compare-price') ||
          '';
      } else {
        price =
          activeQuantityButton?.getAttribute('data-one-time-price') ||
          productFormHost?.getAttribute('data-one-time-price') ||
          '';
        comparePrice =
          activeQuantityButton?.getAttribute('data-one-time-compare-price') ||
          productFormHost?.getAttribute('data-one-time-compare-price') ||
          '';
      }

      if (price) {
        atcPriceElement.textContent = price;
      }

      if (atcComparePriceElement) {
        if (comparePrice && comparePrice !== price) {
          atcComparePriceElement.textContent = comparePrice;
          atcComparePriceElement.classList.add('product__price--compare-active');
        } else {
          atcComparePriceElement.textContent = '';
          atcComparePriceElement.classList.remove('product__price--compare-active');
        }
      }
    }

    /**
     * Dispatch custom event when purchase type changes
     */
    dispatchChangeEvent() {
      const event = new CustomEvent('td-subscription-box:change', {
        bubbles: true,
        detail: {
          scopeKey: TdSubBox.key(this),
          purchaseType: this.purchaseType,
          sellingPlanId: this.purchaseType === 'subscription' ? this.selectedSellingPlanId : null
        }
      });
      
      this.dispatchEvent(event);
      this.dispatchShippingCalloutUpdate();
    }

    dispatchShippingCalloutUpdate() {
      document.dispatchEvent(
        new CustomEvent('td-shipping-callout:update')
      );
    }

    /**
     * Public method to get current state
     * @returns {Object}
     */
    getState() {
      return {
        purchaseType: this.purchaseType,
        sellingPlanId: this.purchaseType === 'subscription' ? this.selectedSellingPlanId : null,
        isSubscription: this.purchaseType === 'subscription'
      };
    }
  }

  customElements.define('td-subscription-box', TdSubscriptionBox);
}


/* ==========================================================================
   TD Quantity Selector 2026 - Quantity/Bundle Button Selector
   Scoped version for the 2026 subscription box
   ========================================================================== */

if (!customElements.get('td-quantity-selector-2026')) {
  class TdQuantitySelector2026 extends HTMLElement {
    constructor() {
      super();
      
      /* Quantity selector purchase state is an object; TdSubscriptionBox uses a string state */
      this.purchaseType = {
        isOneTime: true,
        hasSubscription: false
      };
      
      /* Store observer reference for cleanup */
      this.mutationObserver = null;
      
      /* Store button references */
      this.quantityButtons = [];
      this.bundleButton = null;
      
      /* Store currently selected button */
      this.selectedButton = null;
      
      /* Store form reference */
      this.productForm = null;
      
      /* Reference to subscription box */
      this.subscriptionBox = null;
      this.boundSubscriptionChangeListener = this.handlePurchaseTypeChange.bind(this);
      this.boundBundleSubmitHandler = this.handleBundleSubmit.bind(this);
      this.boundBundleClickHandler = this.handleBundleButtonSubmitClick.bind(this);
      this.sellingPlanInput = null;
      this.submitButton = null;
    }

    /* Nodes below live in the add-to-cart block once the parts are split apart,
       so they are resolved on every access rather than cached. */

    /** @returns {HTMLInputElement|null} */
    get offerTypeInput() {
      return TdSubBox.find(this, '[data-td-offer-type-input]');
    }

    /** @returns {HTMLInputElement|null} */
    get purchaseTypeInput() {
      return TdSubBox.find(this, '[data-td-purchase-type-input]');
    }

    /** @returns {HTMLInputElement|null} */
    get offerSourceInput() {
      return TdSubBox.find(this, '[data-td-offer-source-input]');
    }

    /** @returns {HTMLInputElement|null} */
    get offerProductIdInput() {
      return TdSubBox.find(this, '[data-td-offer-product-id-input]');
    }

    connectedCallback() {
      /* Detect initial state on page load */
      this.detectPurchaseType();
      
      /* Set up listeners for future changes */
      this.setupPurchaseTypeListener();
      
      /* Set up button listeners */
      this.setupButtonListeners();

      /* Find and set initially active button */
      this.setInitialActiveButton();
      
      /* Set up form submission listener */
      this.setupFormListener();
      
      /* Set initial states */
      this.updateButtonDisabledState();
      this.updatePriceContainerVisibility();
      this.updateDiscountValueVisibility();
      this.updateATCPrice();
      this.updateSubscriptionOptionState();
      this.updateOnetimeOnlyBadges();
      this.updateBundleSavingsBadge();
      this.updateTrackingProperties();
      this.dispatchShippingCalloutUpdate();
    }

    disconnectedCallback() {
      /* Clean up observers when component is removed */
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }

      document.removeEventListener('td-subscription-box:change', this.boundSubscriptionChangeListener);
      if (this.productForm) {
        this.productForm.removeEventListener('submit', this.boundBundleSubmitHandler, true);
      }
      if (this.submitButton) {
        this.submitButton.removeEventListener('click', this.boundBundleClickHandler, true);
      }
    }

    /**
     * Gets the subscription box element (lazy lookup)
     * @returns {HTMLElement|null}
     */
    getSubscriptionBox() {
      if (!this.subscriptionBox) {
        this.subscriptionBox = TdSubBox.find(this, 'td-subscription-box');
      }
      return this.subscriptionBox;
    }

    /**
     * Detects whether user has selected one-time purchase or subscription
     * @returns {Object} Object with isOneTime and hasSubscription boolean properties
     */
    detectPurchaseType() {
      const sellingPlanInput = TdSubBox.find(this, 'input[name="selling_plan"]');
      const subscriptionBox = this.getSubscriptionBox();
      
      /* Check subscription box state if available */
      if (subscriptionBox) {
        const state = subscriptionBox.getState?.();
        if (state) {
          this.purchaseType = {
            isOneTime: !state.isSubscription,
            hasSubscription: state.isSubscription
          };
          return this.purchaseType;
        }
      }
      
      /* Fallback to checking selling plan input */
      const hasActiveSubscription = sellingPlanInput && sellingPlanInput.value;
      
      this.purchaseType = {
        isOneTime: !hasActiveSubscription,
        hasSubscription: !!hasActiveSubscription
      };
      
      return this.purchaseType;
    }

    /**
     * Sets up a listener to detect when purchase type changes
     */
    setupPurchaseTypeListener() {
      /* Primary signal: the subscription block announces its own state changes.
         Registered first so it survives a missing add-to-cart block. */
      document.addEventListener('td-subscription-box:change', this.boundSubscriptionChangeListener);

      this.sellingPlanInput = TdSubBox.find(this, 'input[name="selling_plan"]');
      if (!this.sellingPlanInput) return;

      /* Fallback signal: something outside this component wrote the input directly */
      this.mutationObserver = new MutationObserver(() => {
        this.handlePurchaseTypeChange();
      });

      this.mutationObserver.observe(this.sellingPlanInput, {
        attributes: true,
        attributeFilter: ['value']
      });
    }

    /**
     * Handles purchase type changes
     */
    handlePurchaseTypeChange(event) {
      if (event && !TdSubBox.ownsEvent(this, event)) return;

      if (event?.detail?.purchaseType) {
        const isSubscription = event.detail.purchaseType === 'subscription';
        this.purchaseType = {
          isOneTime: !isSubscription,
          hasSubscription: isSubscription
        };
      } else {
        this.detectPurchaseType();
      }
      
      /* Update all states */
      this.updateButtonDisabledState();
      this.updatePriceContainerVisibility();
      this.updateDiscountValueVisibility();
      this.updateATCPrice();
      this.updateOnetimeOnlyBadges();
      this.updateBundleSavingsBadge();
      this.updateTrackingProperties();
      this.dispatchShippingCalloutUpdate();
    }

    /**
     * Sets up event listeners for quantity and bundle buttons
     */
    setupButtonListeners() {
      /* Find all quantity buttons */
      this.quantityButtons = Array.from(this.querySelectorAll('[data-quantity]'));
      
      /* Find bundle button */
      this.bundleButton = this.querySelector('[data-bundle-product-id]');
      
      /* Add click listeners to quantity buttons */
      this.quantityButtons.forEach(button => {
        button.addEventListener('click', () => {
          this.handleButtonClick(button, 'quantity');
        });
      });
      
      /* Add click listener to bundle button if it exists */
      if (this.bundleButton) {
        this.bundleButton.addEventListener('click', () => {
          this.handleButtonClick(this.bundleButton, 'bundle');
        });
      }
    }

    /**
     * Finds and sets the initially active button on page load
     */
    setInitialActiveButton() {
      const activeButton = this.querySelector('.td-quantity-button--active');
      if (activeButton) {
        this.selectedButton = activeButton;
        
        /* Update bundle additional info container class */
        this.updateBundleAdditionalInfoContainer();
        
        /* Update quantity input if it's a quantity button */
        if (activeButton.hasAttribute('data-quantity')) {
          this.updateQuantityInput(activeButton);
        }
        
        /* Update subscription option state based on initial button */
        this.updateSubscriptionOptionState();
      }
    }

    /**
     * Handles button click events
     */
    handleButtonClick(button, buttonType) {
      /* Check if button is disabled */
      if (button.classList.contains('td-quantity-button--disabled')) {
        return;
      }
      
      /* Remove active class from all buttons */
      this.quantityButtons.forEach(btn => {
        btn.classList.remove('td-quantity-button--active');
      });
      if (this.bundleButton) {
        this.bundleButton.classList.remove('td-quantity-button--active');
      }
      
      /* Add active class to clicked button */
      button.classList.add('td-quantity-button--active');
      
      /* Update selected button reference */
      this.selectedButton = button;
      
      /* Update bundle additional info container class */
      this.updateBundleAdditionalInfoContainer();
      
      /* Update subscription option disabled state */
      this.updateSubscriptionOptionState();
      
      /* Update ATC price */
      this.updateATCPrice();
      
      /* Update quantity input if it's a quantity button (not bundle) */
      if (buttonType === 'quantity') {
        this.updateQuantityInput(button);
      }
      
      /* Update subscription box prices */
      this.updateSubscriptionBoxPrices();
      
      /* Update one-time only badges */
      this.updateOnetimeOnlyBadges();
      this.updateTrackingProperties();
      this.dispatchShippingCalloutUpdate();
    }

    /**
     * Keeps private line-item properties synchronized with the selected offer.
     * @returns {void}
     */
    updateTrackingProperties() {
      if (!this.selectedButton) return;

      const offerType = this.selectedButton.getAttribute('data-td-offer-type');
      const offerProductId = this.selectedButton.getAttribute('data-td-offer-product-id');
      const isSubscription = this.purchaseType?.hasSubscription === true;

      const offerTypeInput = this.offerTypeInput;
      if (offerTypeInput) {
        offerTypeInput.value = offerType || '';
      }
      const purchaseTypeInput = this.purchaseTypeInput;
      if (purchaseTypeInput) {
        purchaseTypeInput.value = isSubscription ? 'subscription' : 'one_time';
      }
      const offerProductIdInput = this.offerProductIdInput;
      if (offerProductIdInput) {
        offerProductIdInput.value = offerProductId || '';
      }
    }

    /**
     * Returns attribution properties directly from the current custom cart selection.
     * @returns {Object} Shopify line-item properties.
     */
    getTrackingProperties() {
      const offerType = this.selectedButton?.getAttribute('data-td-offer-type') || '';
      const offerProductId = this.selectedButton?.getAttribute('data-td-offer-product-id') || '';
      const purchaseType = this.purchaseType?.hasSubscription === true ? 'subscription' : 'one_time';

      return {
        _td_offer_type: offerType,
        _td_purchase_type: purchaseType,
        _td_offer_source: this.offerSourceInput?.value || 'subscription_box_2026',
        _td_offer_product_id: offerProductId
      };
    }

    dispatchShippingCalloutUpdate() {
      document.dispatchEvent(
        new CustomEvent('td-shipping-callout:update')
      );
    }

    /**
     * Sets up form submission listener for bundle products
     */
    setupFormListener() {
      this.productForm =
        TdSubBox.host(this)?.querySelector('form[data-type="add-to-cart-form"]') ||
        this.closest('form');
      
      if (!this.productForm) return;
      
      /* Listen for form submission with capture */
      this.productForm.addEventListener('submit', this.boundBundleSubmitHandler, true);

      this.submitButton = this.productForm.querySelector('button[type="submit"]');
      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.boundBundleClickHandler, true);
      }
    }

    handleBundleSubmit(event) {
      if (this.isBundleSelected()) {
        this.handleBundleProductSubmission(event);
      }
    }

    handleBundleButtonSubmitClick(event) {
      if (!this.isBundleSelected()) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.handleBundleProductSubmission(event);
      return false;
    }

    /**
     * Checks if bundle product is currently selected
     */
    isBundleSelected() {
      return this.selectedButton && this.selectedButton.hasAttribute('data-bundle-product-id');
    }

    /**
     * Updates the bundle additional info container class
     */
    updateBundleAdditionalInfoContainer() {
      const container = TdSubBox.find(this, '[data-bundle-additional-info-container]');
      if (container) {
        if (this.isBundleSelected()) {
          container.classList.add('td-quantity-buttons__additional-info-container--active');
        } else {
          container.classList.remove('td-quantity-buttons__additional-info-container--active');
        }
      }
    }

    /**
     * Updates the bundle savings badge for the selected purchase type.
     */
    updateBundleSavingsBadge() {
      const badge = TdSubBox.find(this, '[data-td-bundle-savings-badge]');
      if (!badge) return;

      const textAttribute = this.purchaseType.hasSubscription
        ? 'data-td-subscription-text'
        : 'data-td-one-time-text';
      const nextText = badge.getAttribute(textAttribute);

      if (nextText) {
        badge.textContent = nextText;
      }
    }

    /**
     * Handles bundle product form submission
     */
    handleBundleProductSubmission(event) {
      event.preventDefault();
      event.stopPropagation();

      const bundleVariantId = this.selectedButton?.getAttribute('data-bundle-product-variant-id');

      if (!bundleVariantId) {
        console.error('[Quantity Selector 2026] Bundle variant ID not found');
        return;
      }

      const sellingPlanInput = TdSubBox.find(this, 'input[name="selling_plan"]');
      const sellingPlanId = this.purchaseType.hasSubscription && sellingPlanInput ? sellingPlanInput.value : '';
      const cartData = {
        items: [{
          id: bundleVariantId,
          quantity: 1,
          properties: this.getTrackingProperties()
        }]
      };

      if (sellingPlanId) {
        cartData.items[0].selling_plan = sellingPlanId;
      }

      this.addBundleProductToCart(cartData);
    }

    /**
     * Adds bundle product to cart using Cart API
     */
    async addBundleProductToCart(cartData) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(cartData)
        });

        if (response.ok) {
          await response.json();

          const addToCartEvent = new CustomEvent('add-to-cart', {
            bubbles: true,
            detail: { bundleProduct: true }
          });

          const productFormHost = TdSubBox.host(this);
          if (productFormHost) {
            productFormHost.dispatchEvent(addToCartEvent);
            return;
          }

          const productForm =
            this.productForm ||
            this.closest('form[data-type="add-to-cart-form"]') ||
            document.querySelector('.product__form, form[data-type="add-to-cart-form"]');
          if (productForm) {
            productForm.dispatchEvent(addToCartEvent);
            return;
          }

          const cartDrawer = document.getElementById('site-cart-sidebar');
          if (cartDrawer && typeof cartDrawer.show === 'function') {
            cartDrawer.show();
          }
        } else {
          const errorData = await response.json();
          console.error('[Quantity Selector 2026] Cart error:', errorData);
        }
      } catch (error) {
        console.error('[Quantity Selector 2026] Cart error:', error);
      }
    }

    /**
     * Updates disabled state of subscription option based on selected button's selling plans
     * Disables (not hides) subscription option when bundle without selling plans is selected
     * Also hides subscription content when product has no selling plans
     */
    updateSubscriptionOptionState() {
      const subscriptionBox = this.getSubscriptionBox();
      if (!this.selectedButton || !subscriptionBox) return;
      
      const hasSellingPlans = this.selectedButton.getAttribute('data-has-selling-plans') === 'true';
      const subscriptionOption = subscriptionBox.querySelector('.td-purchase-option--subscription');
      const subscriptionContent = subscriptionBox.querySelector('.td-subscription-content');
      
      if (subscriptionOption) {
        /* pointer-events blocks the mouse only - the radio shares a group with
           the one-time option, so arrow keys would otherwise select it and
           write a selling plan the current selection cannot take. */
        const radio = subscriptionOption.querySelector('input[type="radio"]');
        if (radio) radio.disabled = !hasSellingPlans;

        if (hasSellingPlans) {
          subscriptionOption.classList.remove('td-purchase-option--disabled');
        } else {
          subscriptionOption.classList.add('td-purchase-option--disabled');
          
          /* If subscription was selected, switch to one-time */
          const subscriptionRadio = subscriptionOption.querySelector('input[type="radio"]');
          if (subscriptionRadio && subscriptionRadio.checked) {
            const onetimeRadio = subscriptionBox.querySelector('[data-purchase-type="onetime"]');
            if (onetimeRadio) {
              onetimeRadio.checked = true;
              onetimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }
      }

      subscriptionBox.dispatchEvent(
        new CustomEvent('td-subscription-option:state-change', {
          bubbles: true,
          detail: { hasSellingPlans: hasSellingPlans }
        })
      );
      
      /* Hide/show subscription content based on selling plans */
      if (subscriptionContent) {
        if (hasSellingPlans) {
          subscriptionContent.classList.remove('td-subscription-content--no-plans');
        } else {
          subscriptionContent.classList.add('td-subscription-content--no-plans');
        }
      }
    }

    /**
     * Updates subscription box prices based on selected quantity button
     */
    updateSubscriptionBoxPrices() {
      const subscriptionBox = this.getSubscriptionBox();
      if (!this.selectedButton || !subscriptionBox) return;
      
      const oneTimePrice = this.selectedButton.getAttribute('data-one-time-price') || '';
      const oneTimeComparePrice = this.selectedButton.getAttribute('data-one-time-compare-price') || '';
      const subscriptionPrice = this.selectedButton.getAttribute('data-subscription-price') || '';
      const subscriptionComparePrice = this.selectedButton.getAttribute('data-subscription-compare-price') || '';
      
      /* Update one-time price */
      const onetimePriceEl = subscriptionBox.querySelector('[data-onetime-price]');
      if (onetimePriceEl) {
        onetimePriceEl.textContent = oneTimePrice;
      }
      
      /* Update one-time compare price */
      const onetimeComparePriceEl = subscriptionBox.querySelector('[data-onetime-compare-price]');
      if (onetimeComparePriceEl) {
        if (oneTimeComparePrice) {
          onetimeComparePriceEl.textContent = oneTimeComparePrice;
          onetimeComparePriceEl.style.display = '';
        } else {
          onetimeComparePriceEl.style.display = 'none';
        }
      }
      
      /* Update subscription price */
      const subscriptionPriceEl = subscriptionBox.querySelector('[data-subscription-price]');
      if (subscriptionPriceEl) {
        subscriptionPriceEl.textContent = subscriptionPrice;
      }
      
      /* Update subscription compare price */
      const subscriptionComparePriceEl = subscriptionBox.querySelector('[data-subscription-compare-price]');
      if (subscriptionComparePriceEl) {
        if (subscriptionComparePrice) {
          subscriptionComparePriceEl.textContent = subscriptionComparePrice;
          subscriptionComparePriceEl.style.display = '';
        } else {
          subscriptionComparePriceEl.style.display = 'none';
        }
      }
    }

    /**
     * Updates disabled state of buttons based on purchase type
     * Uses opacity/pointer-events instead of display:none
     */
    updateButtonDisabledState() {
      if (this.purchaseType.hasSubscription) {
        /* Subscription selected - disable buttons without selling plans */
        this.quantityButtons.forEach(button => {
          const hasSellingPlans = button.getAttribute('data-has-selling-plans') === 'true';
          if (hasSellingPlans) {
            button.classList.remove('td-quantity-button--disabled');
          } else {
            button.classList.add('td-quantity-button--disabled');
          }
        });
        
        if (this.bundleButton) {
          const hasSellingPlans = this.bundleButton.getAttribute('data-has-selling-plans') === 'true';
          if (hasSellingPlans) {
            this.bundleButton.classList.remove('td-quantity-button--disabled');
          } else {
            this.bundleButton.classList.add('td-quantity-button--disabled');
          }
        }
      } else {
        /* One-time selected - enable all buttons */
        this.quantityButtons.forEach(button => {
          button.classList.remove('td-quantity-button--disabled');
        });
        
        if (this.bundleButton) {
          this.bundleButton.classList.remove('td-quantity-button--disabled');
        }
      }
    }

    /**
     * Updates one-time only badge visibility
     * Badge shows when: button has no selling plans AND (subscription is selected OR button is active)
     */
    updateOnetimeOnlyBadges() {
      const allButtons = [...this.quantityButtons];
      if (this.bundleButton) {
        allButtons.push(this.bundleButton);
      }
      
      const isSubscriptionSelected = this.purchaseType.hasSubscription;
      
      allButtons.forEach(button => {
        const hasSellingPlans = button.getAttribute('data-has-selling-plans') === 'true';
        const isActive = button.classList.contains('td-quantity-button--active');
        
        /* Show badge if: button has no selling plans AND (subscription selected OR button is active) */
        if (!hasSellingPlans && (isSubscriptionSelected || isActive)) {
          button.classList.add('td-quantity-button--show-onetime-badge');
        } else {
          button.classList.remove('td-quantity-button--show-onetime-badge');
        }
      });
    }

    /**
     * Updates price container visibility based on purchase type
     */
    updatePriceContainerVisibility() {
      const allButtons = [...this.quantityButtons];
      if (this.bundleButton) {
        allButtons.push(this.bundleButton);
      }
      
      allButtons.forEach(button => {
        const oneTimeContainer = button.querySelector('.td-quantity-buttons__one-time-price-container');
        const subscriptionContainer = button.querySelector('.td-quantity-buttons__subscription-price-container');
        
        if (this.purchaseType.isOneTime) {
          /* Show one-time container, hide subscription container */
          if (oneTimeContainer) {
            oneTimeContainer.style.display = 'flex';
          }
          if (subscriptionContainer) {
            subscriptionContainer.style.display = 'none';
          }
        } else if (this.purchaseType.hasSubscription) {
          /* Show subscription container, hide one-time container */
          if (oneTimeContainer) {
            oneTimeContainer.style.display = 'none';
          }
          if (subscriptionContainer) {
            subscriptionContainer.style.display = 'flex';
          }
        }
      });
    }

    /**
     * Updates visibility of discount value divs based on purchase type
     */
    updateDiscountValueVisibility() {
      const allButtons = [...this.quantityButtons];
      if (this.bundleButton) {
        allButtons.push(this.bundleButton);
      }
      
      allButtons.forEach(button => {
        const oneTimeDiscount = button.querySelector('.td-quantity__one-time-discount-value');
        const subscriptionDiscount = button.querySelector('.td-quantity__subscription-discount-value');
        
        if (this.purchaseType.isOneTime) {
          if (oneTimeDiscount) oneTimeDiscount.style.display = 'block';
          if (subscriptionDiscount) subscriptionDiscount.style.display = 'none';
        } else if (this.purchaseType.hasSubscription) {
          if (oneTimeDiscount) oneTimeDiscount.style.display = 'none';
          if (subscriptionDiscount) subscriptionDiscount.style.display = 'block';
        }
      });
    }

    /**
     * Updates the quantity input field based on selected quantity button
     */
    updateQuantityInput(button) {
      const quantityInput = TdSubBox.find(this, '.td-quantity-input');
      if (!quantityInput) return;
      
      const quantity = button.getAttribute('data-quantity');
      if (quantity) {
        quantityInput.value = quantity;
      }
    }

    /**
     * Updates the add to cart button price based on selected button and purchase type
     */
    updateATCPrice() {
      const atcPriceElement = TdSubBox.find(this, '[data-quantity-price]');
      const atcComparePriceElement = TdSubBox.find(this, '[data-quantity-compare-price]');
      
      if (!atcPriceElement || !this.selectedButton) return;
      
      let price = '';
      let comparePrice = '';
      
      if (this.purchaseType.isOneTime) {
        price = this.selectedButton.getAttribute('data-one-time-price') || '';
        comparePrice = this.selectedButton.getAttribute('data-one-time-compare-price') || '';
      } else if (this.purchaseType.hasSubscription) {
        price = this.selectedButton.getAttribute('data-subscription-price') || '';
        comparePrice = this.selectedButton.getAttribute('data-subscription-compare-price') || '';
      }
      
      if (price) {
        atcPriceElement.textContent = price;
      }
      
      if (atcComparePriceElement) {
        if (comparePrice && comparePrice !== price) {
          atcComparePriceElement.textContent = comparePrice;
          atcComparePriceElement.classList.add('product__price--compare-active');
        } else {
          atcComparePriceElement.textContent = '';
          atcComparePriceElement.classList.remove('product__price--compare-active');
        }
      }
    }

    /**
     * Public method to get current purchase type state
     * @returns {Object} Current purchase type state
     */
    getPurchaseType() {
      return { ...this.purchaseType };
    }
  }

  customElements.define('td-quantity-selector-2026', TdQuantitySelector2026);
}
